import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import AppRouter from './router'; // Import AppRouter
import reportWebVitals from './reportWebVitals';
import { UserProvider } from './components/UserContext';
import { logServiceWorker, logDiagnostic, logError } from './utils/diagnosticLogger';

// Track page loads and detect reloads
if (typeof window !== 'undefined') {
  // Log initial page load
  logDiagnostic('page', 'initial-load', {
    url: window.location.href,
    pathname: window.location.pathname,
    referrer: document.referrer,
    navigationType: performance.getEntriesByType('navigation')[0]?.type || 'unknown',
    wasCached: performance.getEntriesByType('navigation')[0]?.transferSize === 0
  });
  
  // Detect if this is a reload (not a fresh navigation)
  if (performance.getEntriesByType('navigation')[0]?.type === 'reload') {
    logDiagnostic('page', 'reload-detected', {
      url: window.location.href,
      timestamp: Date.now()
    });
  }
  
  // Monitor for location changes that might cause reloads
  let lastLocation = window.location.href;
  const locationCheckInterval = setInterval(() => {
    if (window.location.href !== lastLocation) {
      logDiagnostic('page', 'location-changed', {
        from: lastLocation,
        to: window.location.href,
        method: 'unknown'
      });
      lastLocation = window.location.href;
    }
  }, 100);
  
  // Note: Can't override window.location.reload or window.location.href
  // They are read-only properties. We'll detect reloads via:
  // 1. Performance API (navigation type)
  // 2. Page load events
  // 3. Component remounting patterns
}

// Global error handler to prevent app crashes from closing modals
window.addEventListener('error', (event) => {
  // Ignore image load errors - they're handled by onError handlers
  if (event.target && event.target.tagName === 'IMG') {
    console.warn('Image load error (handled):', event.target.src);
    event.preventDefault();
    return false;
  }
  
  // Safari-specific: Handle navigation errors that cause blank pages
  if (event.error && event.error.message && event.error.message.includes('Failed to fetch')) {
    console.warn('Network error detected (possibly Safari navigation issue):', event.error.message);
    // Don't prevent default - let browser handle network errors
    // This prevents Safari from showing blank pages
    return true;
  }
  
  console.error('Global error caught:', event.error);
  logError(event.error, {
    type: 'global-error',
    url: window.location.href,
    pathname: window.location.pathname,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack
  });
  
  // Log blank screen risk
  if (event.error) {
    logDiagnostic('error', 'blank-screen-risk', {
      message: event.error.message,
      stack: event.error.stack,
      url: window.location.href,
      pathname: window.location.pathname,
      timestamp: Date.now()
    });
  }
  
  // Prevent default error handling that might close modals
  event.preventDefault();
  return false;
});

// Handle unhandled promise rejections (common with async image compression)
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  logError(event.reason, {
    type: 'unhandled-rejection',
    url: window.location.href
  });
  // Prevent default error handling
  event.preventDefault();
  return false;
});

// Track page loads and unloads for diagnostic logging
// Log page load
window.addEventListener('load', () => {
  logDiagnostic('page', 'load', {
    url: window.location.href,
    pathname: window.location.pathname,
    referrer: document.referrer,
    navigationType: performance.navigation?.type || 'unknown',
    loadTime: performance.timing ? (performance.timing.loadEventEnd - performance.timing.navigationStart) : 'unknown'
  });
});

// Log page unload (this happens before reload)
window.addEventListener('beforeunload', () => {
  logDiagnostic('page', 'beforeunload', {
    url: window.location.href,
    pathname: window.location.pathname
  });
  // Flush logs immediately
  if (window.flushDiagnosticLogs) {
    window.flushDiagnosticLogs();
  }
});

// Log page visibility changes (might indicate reload)
document.addEventListener('visibilitychange', () => {
  logDiagnostic('page', 'visibilitychange', {
    hidden: document.hidden,
    url: window.location.href
  });
});

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Track when page loaded to prevent immediate reloads
    window.pageLoadTime = Date.now();
    
    logDiagnostic('page', 'sw-registration-start', {
      url: window.location.href,
      timestamp: Date.now()
    });
    
    // Clear reload flag if page loaded successfully (prevents stuck states)
    if (sessionStorage.getItem('sw-reloading') === 'true') {
      // If we get here, the reload completed successfully
      logDiagnostic('page', 'sw-reload-completed', {});
      sessionStorage.removeItem('sw-reloading');
    }
    
    // Register service worker - browser will check for updates automatically
    // The nginx config ensures sw.js is never cached
    logServiceWorker('registration-start', {
      url: window.location.href,
      pathname: window.location.pathname
    });
    
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        console.log('SW registered: ', registration);
        logServiceWorker('registration-success', {
          scope: registration.scope,
          active: registration.active ? registration.active.state : null,
          waiting: registration.waiting ? registration.waiting.state : null
        });
        
        // CRITICAL: Prevent automatic reload when service worker controller changes
        // When a new service worker takes control, browser can auto-reload the page
        // We prevent this to avoid interrupting navigation
        let isReloading = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('Service worker controller changed');
          logServiceWorker('controller-change', {
            currentPath: window.location.pathname,
            timestamp: Date.now()
          });
          // DO NOT reload - let the page continue normally
          // The new service worker will handle future requests
          // Only reload if explicitly needed (user action, not automatic)
        });
        
        // Listen for service worker updates (browser checks automatically)
        registration.addEventListener('updatefound', () => {
          console.log('New service worker found, installing...');
          logServiceWorker('update-found', {
            currentPath: window.location.pathname
          });
          const newWorker = registration.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              logServiceWorker('worker-state-change', {
                state: newWorker.state,
                currentPath: window.location.pathname
              });
              
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New service worker is ready - will activate on next page load naturally
                  console.log('New service worker installed. It will activate on next page load or when all tabs are closed.');
                  logServiceWorker('worker-installed-waiting', {
                    currentPath: window.location.pathname
                  });
                  // DO NOT reload - prevent automatic reload
                } else {
                  // First time installation
                  console.log('Service worker installed for the first time.');
                  logServiceWorker('worker-installed-first-time', {});
                }
              }
            });
          }
        });
        
        // Only check for updates when user returns to app (not on every page load)
        // This prevents interrupting active navigation
        let lastUpdateCheck = 0;
        const UPDATE_CHECK_INTERVAL = 60000; // 60 seconds minimum between checks
        
        document.addEventListener('visibilitychange', () => {
          // Only check when page becomes visible AND enough time has passed
          if (!document.hidden && Date.now() - lastUpdateCheck > UPDATE_CHECK_INTERVAL) {
            lastUpdateCheck = Date.now();
            logServiceWorker('visibility-change-update-check', {
              currentPath: window.location.pathname
            });
            // Check for updates (browser also checks automatically, but this ensures we catch them)
            registration.update().catch(err => {
              console.log('Service worker update check failed:', err);
              logServiceWorker('update-check-failed', { error: err.message });
            });
          }
        });
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
        logServiceWorker('registration-failed', {
          error: registrationError.message,
          stack: registrationError.stack
        });
      });
    
    // DISABLED: Automatic update checks on focus/visibility
    // These were causing white screen issues when navigating to job list
    // Updates will still happen via the periodic check (every 10 minutes)
    // and when the page is manually refreshed
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <UserProvider>
      <AppRouter /> {/* Use AppRouter instead of App */}
    </UserProvider>
  </React.StrictMode>
);

reportWebVitals();